import { useEffect, useMemo } from 'react';

import { Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectProject,
  selectTypicalMonth,
  setTypicalMonth,
} from 'state/workflowSlice';

import { MonthInfo } from 'appTypes';
import { sortTypicalMonths } from 'appUtils';
import { displayTypicalMonth } from 'features/common/datetimeUtils';

import styles from './Taskbar.module.css';

export function GenericBaseMonthPicker({
  querier,
  fullWidth,
  value,
  setValue,
  disabled = false,
  classes = {},
  menuItemLabel,
}: {
  querier: UseQuery<any>;
  fullWidth?: boolean;
  value: string;
  setValue: (v: string) => void;
  disabled?: boolean;
  classes?: {
    paper?: string;
    menuItem?: string;
  };
  menuItemLabel?: (info: MonthInfo) => string;
}) {
  const project_slug = useAppSelector(selectProject);
  const {
    data: typicalMonthsInfo = [],
    isLoading,
    isSuccess,
    isFetching,
    isError,
  } = querier(project_slug, { skip: !project_slug });

  const sortedMonths = useMemo(
    () => (typicalMonthsInfo ? sortTypicalMonths(typicalMonthsInfo) : []),
    [typicalMonthsInfo],
  );

  const typicalMonthsList = useMemo(
    () => sortedMonths.map((info) => info.typicalMonth),
    [sortedMonths],
  );

  const formatMenuItemLabel =
    menuItemLabel ??
    ((info: MonthInfo) => displayTypicalMonth(info.typicalMonth));

  const handleChange = (event) => {
    const newTypicalMonth = event.target.value;
    console.log(
      `TypicalMonthPicker handleChange newTypicalMonth: ${newTypicalMonth}`,
    );
    setValue(newTypicalMonth);
  };

  useEffect(() => {
    if (typicalMonthsList.length > 0) {
      if (!value || !typicalMonthsList.includes(value)) {
        console.log(
          'TypicalMonthPicker setting empty/unavailable typicalMonth to most recent month',
        );
        setValue(typicalMonthsList[typicalMonthsList.length - 1]);
      }
    }
  }, [typicalMonthsList, typicalMonthsInfo, setValue, value]);

  let content;

  if (isLoading || isFetching) {
    content = (
      <Typography variant="body2" margin="auto">
        loading typical days...
      </Typography>
    );
  } else if (isSuccess) {
    content = (
      <Select
        className={
          fullWidth
            ? styles.task_bar_select_fullwidth
            : styles.task_bar_select_wide
        }
        value={value || ''}
        onChange={handleChange}
        size="small"
        disabled={disabled}
        MenuProps={{ PaperProps: { className: classes?.paper } }}
      >
        {sortedMonths.map((option) => (
          <MenuItem
            className={classes?.menuItem}
            key={option.typicalMonth}
            value={option.typicalMonth}
          >
            {formatMenuItemLabel(option)}
          </MenuItem>
        ))}
      </Select>
    );
  } else if (isError) {
    content = <span>Could not load dates.</span>;
  }
  return content;
}

export function BaseMonthPicker({
  querier,
  fullWidth = false,
  disabled = false,
  classes = {},
  menuItemLabel,
}: {
  querier: UseQuery<any>;
  fullWidth?: boolean;
  disabled?: boolean;
  classes?: {
    paper?: string;
    menuItem?: string;
  };
  menuItemLabel?: (info: MonthInfo) => string;
}) {
  const typicalMonth = useAppSelector(selectTypicalMonth);
  const dispatch = useAppDispatch();
  return (
    <GenericBaseMonthPicker
      classes={classes}
      querier={querier}
      fullWidth={fullWidth}
      value={typicalMonth}
      setValue={(v) => {
        dispatch(setTypicalMonth(v));
      }}
      disabled={disabled}
      menuItemLabel={menuItemLabel}
    />
  );
}
